import React, { useState, useEffect } from "react";
import {
  FiThumbsUp,
  FiMessageCircle,
  FiShare2,
  FiRepeat,
  FiBookmark,
} from "react-icons/fi";
import { postApi } from "../../services/api/postApi";
import { useOptimisticUpdate } from "../../hooks/useOptimisticUpdate";
import SavePostModal from "./SavePostModal";
import RepostModal from "./RepostModal";
import "./PostActions.css";

// Action button configuration
const actionButtons = [
  { id: "like", icon: FiThumbsUp, label: "Like", activeColor: "blue" },
  {
    id: "comment",
    icon: FiMessageCircle,
    label: "Comment",
    activeColor: "purple",
  },
  { id: "share", icon: FiShare2, label: "Share", activeColor: "cyan" },
  { id: "repost", icon: FiRepeat, label: "Repost", activeColor: "green" },
  { id: "save", icon: FiBookmark, label: "Save", activeColor: "amber" },
];

const PostActions = ({ post, onUpdate, onCommentClick }) => {
  const [liked, setLiked] = useState(post.liked || false);
  const [likeCount, setLikeCount] = useState(post.likeCount || 0);
  const [saved, setSaved] = useState(post.saved || false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showRepostModal, setShowRepostModal] = useState(false);
  const [repostLoading, setRepostLoading] = useState(false);
  const { optimisticUpdate } = useOptimisticUpdate();

  // Sync state when post data changes
  useEffect(() => {
    setLiked(post.liked || false);
    setLikeCount(post.likeCount || 0);
    setSaved(post.saved || false);
  }, [post.liked, post.likeCount, post.saved, post._id]);

  // Handle Like with Optimistic Update
  const handleLike = async () => {
    const previousLiked = liked;
    const previousCount = likeCount;

    await optimisticUpdate({
      updateFn: () => {
        setLiked(!liked);
        setLikeCount(liked ? likeCount - 1 : likeCount + 1);
      },
      apiFn: () => postApi.toggleLike(post._id),
      rollbackFn: () => {
        setLiked(previousLiked);
        setLikeCount(previousCount);
      },
      onSuccess: (response) => {
        const { liked: newLiked, likeCount: newCount } = response.data;
        setLiked(newLiked);
        setLikeCount(newCount);
        onUpdate(post._id, { liked: newLiked, likeCount: newCount });
      },
      onError: () => {},
    });
  };

  // Handle Save/Bookmark
  const handleSave = async () => {
    if (saved) {
      const previousSaved = saved;
      await optimisticUpdate({
        updateFn: () => setSaved(false),
        apiFn: () => postApi.unsavePost(post._id),
        rollbackFn: () => setSaved(previousSaved),
        onSuccess: () => {
          setSaved(false);
          onUpdate(post._id, { saved: false });
        },
        onError: () => {},
      });
    } else {
      setShowSaveModal(true);
    }
  };

  const handleSaveSuccess = () => {
    setSaved(true);
    onUpdate(post._id, { saved: true });
  };

  // Handle Repost
  const handleRepost = async (repostText) => {
    setRepostLoading(true);
    try {
      await postApi.repost(post._id, repostText);
      setShowRepostModal(false);
    } catch (error) {
      // Error handled silently
    } finally {
      setRepostLoading(false);
    }
  };

  // Handle Share
  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: `Post by ${post.author?.name}`,
          text: post.content || "Check out this post",
          url: `${window.location.origin}/posts/${post._id}`,
        });
      } else {
        await navigator.clipboard.writeText(
          `${window.location.origin}/posts/${post._id}`
        );
      }
    } catch (error) {
      // Silent fail
    }
  };

  // Handle action click
  const handleAction = (actionId) => {
    switch (actionId) {
      case "like":
        handleLike();
        break;
      case "comment":
        onCommentClick();
        break;
      case "share":
        handleShare();
        break;
      case "repost":
        setShowRepostModal(true);
        break;
      case "save":
        handleSave();
        break;
      default:
        break;
    }
  };

  // Check if action is active
  const isActive = (actionId) => {
    if (actionId === "like") return liked;
    if (actionId === "save") return saved;
    return false;
  };

  return (
    <>
      <div className="cbx-post-actions">
        {actionButtons.map((action) => {
          const Icon = action.icon;
          const active = isActive(action.id);

          return (
            <button
              key={action.id}
              className={`cbx-action-btn cbx-action-btn--${action.id} ${
                active ? "cbx-action-btn--active" : ""
              }`}
              onClick={() => handleAction(action.id)}
            >
              <Icon
                className="cbx-action-icon"
                fill={active ? "currentColor" : "none"}
                stroke="currentColor"
              />
              <span className="cbx-action-label">{action.label}</span>
            </button>
          );
        })}
      </div>

      {/* Save Post Modal */}
      <SavePostModal
        isOpen={showSaveModal}
        onClose={() => setShowSaveModal(false)}
        postId={post._id}
        onSuccess={handleSaveSuccess}
      />

      {/* Repost Modal */}
      <RepostModal
        isOpen={showRepostModal}
        onClose={() => setShowRepostModal(false)}
        onRepost={handleRepost}
        originalPost={post}
        loading={repostLoading}
      />
    </>
  );
};

export default PostActions;
